import offlineService from './offline';

const API_BASE_URL = '/api';

const isMutation = (method) => ['POST', 'PUT', 'DELETE', 'PATCH'].includes(method);

class FinanceService {
  getToken() {
    return localStorage.getItem('auth_token');
  }

  // Helper: build FormData from object (skip null/undefined)
  buildFormData(data) {
    const fd = new FormData();
    Object.entries(data).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        fd.append(key, value);
      }
    });
    return fd;
  }

  // Helper: check if value is a File object
  hasFile(data) {
    return Object.values(data).some(v => v instanceof File);
  }

  async request(endpoint, options = {}) {
    const url = `${API_BASE_URL}${endpoint}`;
    const token = this.getToken();

    const isFormData = options.body instanceof FormData;

    const config = {
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
        ...options.headers,
      },
      ...options,
    };

    // Don't stringify FormData
    if (isFormData) {
      delete config.headers['Content-Type'];
    } else if (config.body && typeof config.body === 'object' && !(config.body instanceof FormData)) {
      config.body = JSON.stringify(config.body);
    }

    try {
      const response = await fetch(url, config);
      const data = await response.json();
      if (!response.ok) {
        if (response.status === 401 && data.error && data.error.includes('Token expired')) {
          window.dispatchEvent(new CustomEvent('auth:token-expired'));
        }
        throw new Error(data.error || `HTTP error! status: ${response.status}`);
      }
      if (!isMutation(options.method)) {
        offlineService.setCached(url, data).catch(() => {});
      }
      return data;
    } catch (error) {
      if (error instanceof TypeError && error.message.includes('fetch')) {
        const method = options.method || 'GET';
        if (isMutation(method)) {
          const bodyData = typeof options.body === 'string' ? JSON.parse(options.body) : options.body;
          await offlineService.queueMutation(method, url, bodyData);
          return { _offline: true, _queued: true, message: 'Data akan dikirim saat koneksi kembali' };
        } else {
          const cached = await offlineService.getCached(url);
          if (cached) return cached;
        }
      }
      console.error('Finance API request failed:', error);
      throw error;
    }
  }

  // Akun (Account) operations
  async getAkun(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/akun${query ? `?${query}` : ''}`);
  }

  async getAkunById(id) {
    return this.request(`/akun/${id}`);
  }

  async createAkun(data) {
    return this.request('/akun', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateAkun(id, data) {
    return this.request(`/akun/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteAkun(id) {
    return this.request(`/akun/${id}`, {
      method: 'DELETE',
    });
  }

  // Transaksi (Transaction) operations
  async getTransaksi(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/transaksi${query ? `?${query}` : ''}`);
  }

  async createTransaksi(data) {
    const body = this.hasFile(data) ? this.buildFormData(data) : data;
    return this.request('/transaksi', {
      method: 'POST',
      body,
    });
  }

  async updateTransaksi(id, data) {
    const body = this.hasFile(data) ? this.buildFormData(data) : data;
    return this.request(`/transaksi/${id}`, {
      method: 'PUT',
      body,
    });
  }

  async deleteTransaksi(id) {
    return this.request(`/transaksi/${id}`, {
      method: 'DELETE',
    });
  }

  // Restore deleted transaction
  async restoreTransaksi(id) {
    return this.request(`/transaksi/${id}/restore`, {
      method: 'POST',
    });
  }

  // Get deleted transactions (admin)
  async getDeletedTransaksi(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/transaksi/deleted${query ? `?${query}` : ''}`);
  }

  // Reports
  async getJurnal(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/transaksi/jurnal${query ? `?${query}` : ''}`);
  }

  async getLaporan(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/transaksi/laporan${query ? `?${query}` : ''}`);
  }
}

export const financeService = new FinanceService();
export default financeService;
